/**
 * The client's onboarding form (crossmarc/steward-app#1765, docs/35 "The onboarding form and the
 * SSN", design v2 "Option B"): what the client types before Steward is set up, held in the
 * FIRM's workspace as two rows keyed by the `advisor-client` id.
 *
 *   - `onboarding-details/{clientId}`: the client's answers, one flat bag of keys. Written ONLY
 *     by appcore's own `/me/onboarding-forms/{firmWorkspaceId}/...` routes, which take the
 *     client id from the onboarding signup grant (never from the request) and confine a save to
 *     one section's keys. Read by firm staff through the façade's
 *     `GET /advisor/client-records/{clientId}/onboarding`.
 *   - `onboarding-prefill/{clientId}`: what the snapshot already said, written by the intake
 *     writer over `/system/*` and read by the client's own form GET. No firm route exposes it.
 *
 * ## The key vocabulary is appcore's, copied
 *
 * appcore decides which section a key belongs to by its NAME: a key belongs to section `s` iff
 * it is `s_<Name>` with Name matching {@link ONBOARDING_KEY_NAME}
 * (`packages/core/src/db/onboarding-form-repository.ts`, `onboardingSectionOfKey`). The pack's
 * strict schema below is the list of which such keys EXIST. So a key declared here that is not
 * shaped `<section>_<Name>` could never be saved, and a server-owned key missing from here would
 * make appcore's send or tax-id write fail validation AFTER it had spent its rate quota.
 * `onboardingForm.test.ts` pins both, against a copy of appcore's two lists
 * ({@link ONBOARDING_FORM_SECTIONS}, {@link ONBOARDING_SERVER_OWNED_KEYS}); a change on either
 * side needs the other.
 *
 * ## No identity number here, ever
 *
 * The SSN never enters this row. It is sealed into appcore's client identity record by the
 * tax-id route, and this row carries only the server-owned `identityProvidedClient` /
 * `identityProvidedSpouse` booleans, which is all that staff without reveal authority are shown
 * (design v2 open question 3). The date of birth IS here (a `date`), as the mocks show it to
 * the firm unmasked.
 *
 * ## Children (crossmarc/steward-app#1812), and CPA/attorney's removal (crossmarc/steward-app#1850)
 *
 * `children` is two index-aligned string lists, `children_names` and `children_dateOfBirths`
 * (child N's name is `children_names[N]`, their date of birth `children_dateOfBirths[N]`),
 * matching how `intakeSchema.ts`'s `spousePartner.children` already collects a name and a date
 * of birth per child (its `gender` is not carried into onboarding; nothing here asks for it).
 * appcore validates each array independently, generically, as "a short list of text"
 * (`ONBOARDING_ARRAY_MAX_ITEMS`, `ONBOARDING_ARRAY_ITEM_MAX` in `onboarding-form-repository.ts`);
 * there is no server-side check that the two children arrays stay the same length, so
 * `onboardingPrefillFromDraftSections` pads a missing name or date of birth with `''` rather
 * than skipping the slot, to keep prefilled rows aligned by index.
 *
 * `#1812` also added `cpa` and `attorney` (each a name and a firm, the same shape as
 * `trustedContact`). Matt ruled (2026-09-26, #1850) that this was a mistake: RIPL's signed-off
 * mocks never drew a CPA or attorney section, so `#1812` added them on a misinferred prefill
 * list, and `#1850` removes them from the CLIENT-FACING form and the firm's tab. **The schema
 * below (`ONBOARDING_FORM_SECTIONS`, `ONBOARDING_FORM_FIELDS`, `ONBOARDING_SECTION_SAVED_KEYS`,
 * `ONBOARDING_PREFILL_KEYS`) still declares `cpa`/`attorney`, on purpose** — dev and prod already
 * hold onboarding rows written under #1812 with cpa/attorney values and `cpaSaved`/
 * `attorneySaved` markers, and appcore's schema is strict, so a pack that stopped declaring
 * these fields would 400 the next save of ANY section on one of those rows. Removing them from
 * the schema (and from appcore's copies) is tracked separately (crossmarc/steward-app#1853) and
 * must happen only after every live row's cpa/attorney data has been migrated or the rows
 * confirmed gone. Client-facing code instead uses {@link ONBOARDING_CLIENT_FORM_SECTIONS}, the
 * subset of {@link ONBOARDING_FORM_SECTIONS} the client form still shows.
 *
 * ## The per-section "saved" marker (crossmarc/steward-app#1812)
 *
 * A section a client cleared completely leaves zero surviving keys, which used to be
 * indistinguishable from a section never opened, so {@link mergeOnboardingPrefill} brought the
 * prefill back on the next load. appcore's `ONBOARDING_SECTION_SAVED_KEYS` (a copy, like the two
 * lists above) names a server-owned boolean per section that appcore's own `saveOnboardingSection`
 * now stamps `true` on EVERY save of that section, including an all-blank one, so
 * {@link mergeOnboardingPrefill} can tell "cleared" from "never touched" even when a save left no
 * other key behind.
 */

import { resolveAnswers, type SourceAnswer } from './answerPrecedence.js';

export const ONBOARDING_DETAILS_TYPE_CODE = 'onboarding-details';
export const ONBOARDING_PREFILL_TYPE_CODE = 'onboarding-prefill';

/** appcore's section list, in form order. A copy: see the module header. */
export const ONBOARDING_FORM_SECTIONS = [
  'client', 'spouse', 'children', 'address', 'trustedContact', 'cpa', 'attorney', 'beneficiary', 'about',
  'requests',
] as const;
export type OnboardingFormSection = (typeof ONBOARDING_FORM_SECTIONS)[number];

/**
 * The sections the CLIENT-FACING form (and the firm's Onboarding details tab) actually shows,
 * in form order: {@link ONBOARDING_FORM_SECTIONS} minus `cpa` and `attorney`
 * (crossmarc/steward-app#1850 — see the module header) and minus `requests`. `cpa` and
 * `attorney` stay in the wider list above because appcore's pack schema and existing rows still
 * declare them; nothing client-facing should iterate that wider list directly.
 *
 * `requests` (crossmarc/steward-app#2282) is in the wider list because appcore saves it as a
 * section, but it is never an ASKED section: the firm's invitation does not choose it, it holds
 * the client's answers to the firm's information requests rather than a step of the form, and
 * so it is outside every count, step list and "complete" reading built on this list.
 * The firm's pipeline stage reads row existence, not this list: a row whose only saved content is
 * the `requests` section yields no onboarding summary on the client list (`isRequestsOnlyRow` in
 * the façade's `clientRecords.ts`), so the stage reads as before the row existed. One case moves
 * the other way, stated plainly: a row with no markers, no values, no flags and no `sentAt` (a
 * tax id set and then removed, or an all-blank save from before the markers) shows as
 * "onboarding" today, and once `requestsSaved` is added to it the stage reads as before the row
 * existed.
 * `onboardingForm.test.ts` pins that it is absent here and from {@link clientFormSectionsFor}'s
 * output for every ask.
 */
export const ONBOARDING_CLIENT_FORM_SECTIONS = ONBOARDING_FORM_SECTIONS.filter(
  (s): s is Exclude<OnboardingFormSection, 'cpa' | 'attorney' | 'requests'> =>
    s !== 'cpa' && s !== 'attorney' && s !== 'requests',
);
export type OnboardingClientFormSection = (typeof ONBOARDING_CLIENT_FORM_SECTIONS)[number];

/**
 * The client form sections a firm's invitation asked for (crossmarc/steward-app#1882), in form
 * order. `client` and `address` are not optional on the invitation, so they are always shown;
 * `spouse` asks for the `spouse` AND `children` sections together. `asked` is the grant's
 * `askedSections`; absent (an older grant, or an older server) shows every section.
 */
export function clientFormSectionsFor(asked: readonly string[] | null | undefined): OnboardingClientFormSection[] {
  if (!asked) return [...ONBOARDING_CLIENT_FORM_SECTIONS];
  const shown = new Set<OnboardingClientFormSection>(['client', 'address']);
  if (asked.includes('spouse')) { shown.add('spouse'); shown.add('children'); }
  if (asked.includes('trustedContact')) shown.add('trustedContact');
  if (asked.includes('beneficiaries')) shown.add('beneficiary');
  if (asked.includes('aboutYou')) shown.add('about');
  return ONBOARDING_CLIENT_FORM_SECTIONS.filter((s) => shown.has(s));
}

export const ONBOARDING_SENT_AT_KEY = 'sentAt';
/**
 * The server-owned "identity provided" flags. **A flag means the client ENTERED a number
 * through the onboarding form** (appcore's /me tax-id route sets it in the same transaction as
 * the sealed record, and clears it with the record). It does NOT mean "a number is stored for
 * this client": appcore's firm-side advisor-client identity routes write or remove the sealed
 * record without touching these flags, so a number an advisor entered there leaves the flag
 * false, and one an advisor removed there can leave it true.
 *
 * The PRECONDITION this carried is now met (docs/43 slice 9, 2026-11-02): the façade relays the
 * firm-side masked read and staff write (`handlers/clientStaffIdentity.ts`; the DELETE still not),
 * and the firm's Onboarding details tab (`src/advisor/ClientOnboardingDetails.tsx`) now sets
 * "Provided" from `GET …/identities` (`withStoredIdentities`), falling back to this flag only while
 * that read is loading or failed. The relayed set is pinned in
 * `services/steward-app-api/src/__tests__/onboardingForm.test.ts`. The client's own form already
 * derived "Provided" from the identities it reads, not from this flag.
 */
export const ONBOARDING_IDENTITY_PROVIDED_KEYS = {
  client: 'identityProvidedClient',
  spouse: 'identityProvidedSpouse',
} as const;
/**
 * appcore's per-section "saved" marker keys. A copy: see the module header, "The per-section
 * 'saved' marker". Each is stamped `true` by every save of that section, including an all-blank
 * one. No SPA-built section-save call can name one of these keys (each fails
 * {@link onboardingSectionOfKey}'s underscore requirement, so `sectionPayload` in
 * `OnboardingForm.tsx` never emits it), but that is this repo's client code, not a guarantee
 * about the wire: the façade's own section-save schema (`services/steward-app-api/src/handlers
 * /onboardingForm.ts`) forwards an arbitrary key map to appcore unfiltered, so the actual
 * rejection of a client request that names one of these keys directly is enforced server-side,
 * in appcore (crossmarc/appcore#764), not by anything in this repo. Reviewed 2026-09-26.
 */
export const ONBOARDING_SECTION_SAVED_KEYS: Readonly<Record<OnboardingFormSection, string>> = {
  client: 'clientSaved',
  spouse: 'spouseSaved',
  children: 'childrenSaved',
  address: 'addressSaved',
  trustedContact: 'trustedContactSaved',
  cpa: 'cpaSaved',
  attorney: 'attorneySaved',
  beneficiary: 'beneficiarySaved',
  about: 'aboutSaved',
  requests: 'requestsSaved',
};
/** appcore's server-owned keys: written only by send, the tax-id routes, and section saves. A copy. */
export const ONBOARDING_SERVER_OWNED_KEYS: readonly string[] = [
  ONBOARDING_SENT_AT_KEY, ONBOARDING_IDENTITY_PROVIDED_KEYS.client, ONBOARDING_IDENTITY_PROVIDED_KEYS.spouse,
  ...Object.values(ONBOARDING_SECTION_SAVED_KEYS),
];

/** appcore's `KEY_NAME`: the part of a section key after `<section>_`. A copy. */
export const ONBOARDING_KEY_NAME = /^[A-Za-z][A-Za-z0-9]{0,63}$/;

/** appcore's per-value bounds (`ONBOARDING_STRING_MAX` and friends). The SPA mirrors them. */
export const ONBOARDING_STRING_MAX = 4000;
/** appcore's `ONBOARDING_ARRAY_MAX_ITEMS`: items in one list answer. A copy, read from appcore origin/main 997f437 on 2026-10-09. */
export const ONBOARDING_ARRAY_MAX_ITEMS = 50;
/** appcore's `ONBOARDING_ARRAY_ITEM_MAX`: UTF-16 units in one list item, unless named below. A copy, same source (997f437, 2026-10-09). */
export const ONBOARDING_ARRAY_ITEM_MAX = 500;
/** appcore's `ONBOARDING_ARRAY_ITEM_MAX_BY_KEY`: the exact keys whose list items may be longer. A copy, same source (997f437, 2026-10-09). */
export const ONBOARDING_ARRAY_ITEM_MAX_BY_KEY: Readonly<Record<string, number>> = {
  requests_answerTexts: 4000,
};
/**
 * appcore's `ONBOARDING_REQUESTS_SECTION_MAX_BYTES`: the UTF-8 length of `JSON.stringify` of
 * one save of the `requests` section's values. A copy, same source (997f437, 2026-10-09). The worst-case size of the whole
 * `onboarding-details` row rests on it; `onboardingForm.test.ts` recomputes that from the pack
 * type, so a field added to the type shows there.
 */
export const ONBOARDING_REQUESTS_SECTION_MAX_BYTES = 64 * 1024;

/** The section a key belongs to, or null. Same rule as appcore's `onboardingSectionOfKey`. */
export function onboardingSectionOfKey(key: string): OnboardingFormSection | null {
  const idx = key.indexOf('_');
  if (idx <= 0) return null;
  const section = key.slice(0, idx);
  return (ONBOARDING_FORM_SECTIONS as readonly string[]).includes(section) && ONBOARDING_KEY_NAME.test(key.slice(idx + 1))
    ? (section as OnboardingFormSection)
    : null;
}

type FieldKind = 'text' | 'longText' | 'date' | 'yesNo' | 'stringList';

/** One answer the form asks for. `label` is the firm-side label and the pack field label. */
export interface OnboardingFieldSpec {
  key: string;
  kind: FieldKind;
  label: string;
}

const person = (s: 'client' | 'spouse'): OnboardingFieldSpec[] => [
  { key: `${s}_firstName`, kind: 'text', label: 'Legal first name' },
  { key: `${s}_middleName`, kind: 'text', label: 'Middle name' },
  { key: `${s}_lastName`, kind: 'text', label: 'Legal last name' },
  { key: `${s}_dateOfBirth`, kind: 'date', label: 'Date of birth' },
  { key: `${s}_employer`, kind: 'text', label: 'Employer' },
  { key: `${s}_title`, kind: 'text', label: 'Title' },
  { key: `${s}_phone`, kind: 'text', label: 'Phone' },
  { key: `${s}_email`, kind: 'text', label: 'Email' },
];

const addressLines = (prefix: string, what: string): OnboardingFieldSpec[] => [
  { key: `address_${prefix}Street`, kind: 'text', label: `${what}: street` },
  { key: `address_${prefix}City`, kind: 'text', label: `${what}: city` },
  { key: `address_${prefix}State`, kind: 'text', label: `${what}: state` },
  { key: `address_${prefix}PostalCode`, kind: 'text', label: `${what}: ZIP code` },
];

/** Every answer, grouped by section, in form order. The pack schema is derived from this. */
export const ONBOARDING_FORM_FIELDS: Readonly<Record<OnboardingFormSection, readonly OnboardingFieldSpec[]>> = {
  client: person('client'),
  spouse: [
    { key: 'spouse_included', kind: 'yesNo', label: 'Include a spouse or partner' },
    ...person('spouse'),
  ],
  // Two index-aligned lists rather than one list of objects, matching how appcore's flat
  // attribute bag stores every other section. See the module header, "Children ... and
  // CPA/attorney's removal".
  children: [
    { key: 'children_names', kind: 'stringList', label: "Children's names" },
    { key: 'children_dateOfBirths', kind: 'stringList', label: "Children's dates of birth" },
  ],
  address: [
    ...addressLines('legal', 'Legal address'),
    { key: 'address_mailingSameAsLegal', kind: 'yesNo', label: 'Mailing address is the same as legal' },
    ...addressLines('mailing', 'Mailing address'),
    { key: 'address_spouseSameAsClient', kind: 'yesNo', label: 'Spouse lives at the same address' },
    ...addressLines('spouseLegal', 'Spouse legal address'),
  ],
  trustedContact: [
    // The chosen family member: 'spouse' or 'child:<index>'; absent means someone else. See
    // `onboardingTrustedContact.ts`, whose resolver every reader goes through (#1954).
    { key: 'trustedContact_member', kind: 'text', label: 'Trusted contact is a family member' },
    { key: 'trustedContact_memberName', kind: 'text', label: 'Trusted contact family member name' },
    { key: 'trustedContact_name', kind: 'text', label: 'Name' },
    { key: 'trustedContact_relationship', kind: 'text', label: 'Relationship' },
    { key: 'trustedContact_phone', kind: 'text', label: 'Phone' },
    { key: 'trustedContact_email', kind: 'text', label: 'Email' },
  ],
  cpa: [
    { key: 'cpa_name', kind: 'text', label: 'Name' },
    { key: 'cpa_firm', kind: 'text', label: 'Firm' },
  ],
  attorney: [
    { key: 'attorney_name', kind: 'text', label: 'Name' },
    { key: 'attorney_firm', kind: 'text', label: 'Firm' },
  ],
  beneficiary: [
    { key: 'beneficiary_primary', kind: 'longText', label: 'Primary beneficiaries' },
    { key: 'beneficiary_contingent', kind: 'longText', label: 'Contingent beneficiaries' },
    { key: 'beneficiary_notes', kind: 'longText', label: 'Anything else about your wishes' },
  ],
  // Named after `client-about`'s eleven keys (`clientAbout.ts`), so #1768 can copy each answer
  // across one for one. That copy is NOT built here.
  about: [
    { key: 'about_anniversary', kind: 'text', label: 'Anniversary' },
    { key: 'about_dreams', kind: 'longText', label: 'Dreams' },
    { key: 'about_favoriteMedia', kind: 'longText', label: 'Favorite books, shows and music' },
    { key: 'about_hobbies', kind: 'longText', label: 'Hobbies' },
    { key: 'about_sportsPlayed', kind: 'text', label: 'Sports you play' },
    { key: 'about_favoriteTeams', kind: 'text', label: 'Favorite teams' },
    { key: 'about_foodAndDrink', kind: 'longText', label: 'Food and drink' },
    { key: 'about_allergies', kind: 'text', label: 'Allergies' },
    { key: 'about_almaMater', kind: 'text', label: 'Alma mater' },
    { key: 'about_milestones', kind: 'longText', label: 'Milestones coming up' },
    { key: 'about_dislikes', kind: 'longText', label: 'Dislikes' },
  ],
  // The client's answers to the firm's information requests: two index-aligned lists, the id of
  // each request answered and the text typed for it. appcore holds them to the item caps and
  // the byte budget above. Not a step of the form: see {@link ONBOARDING_CLIENT_FORM_SECTIONS}.
  requests: [
    { key: 'requests_answerIds', kind: 'stringList', label: 'Requests answered (ids)' },
    { key: 'requests_answerTexts', kind: 'stringList', label: 'Answers typed to requests' },
  ],
};

/**
 * The one field the form used to ask as "Employer and title" (crossmarc/steward-app#1904), split
 * into `_employer` and `_title`. No form page asks it any more and no new write carries it, but
 * dev rows written before the split still hold it, and appcore validates a whole details row
 * against the pack's strict schema on every save: dropping the field from the schema would make
 * every later save of such a row fail. So it stays DECLARED (pack fields and section keys, hence
 * firm-visible) and is READ through {@link withLegacyEmployerTitle}, and it is not in
 * {@link ONBOARDING_FORM_FIELDS}, so a section save (which replaces the section's keys) retires it.
 */
export const ONBOARDING_LEGACY_EMPLOYER_TITLE_KEYS = ['client_employerTitle', 'spouse_employerTitle'] as const;
const LEGACY_SPECS: OnboardingFieldSpec[] = ONBOARDING_LEGACY_EMPLOYER_TITLE_KEYS.map((key) => (
  { key, kind: 'text', label: 'Employer and title (before the split)' }
));

export const ONBOARDING_SECTION_KEYS: readonly string[] = [
  ...ONBOARDING_FORM_SECTIONS.flatMap((s) => ONBOARDING_FORM_FIELDS[s].map((f) => f.key)),
  ...ONBOARDING_LEGACY_EMPLOYER_TITLE_KEYS,
];

/** The keys the intake writer may fill from a snapshot (a subset of the section keys). */
export const ONBOARDING_PREFILL_KEYS = [
  'client_firstName', 'client_lastName', 'client_email', 'client_phone', 'client_employer', 'client_title',
  'spouse_included', 'spouse_firstName', 'spouse_lastName', 'spouse_email', 'spouse_phone', 'spouse_employer', 'spouse_title',
  'children_names', 'children_dateOfBirths', 'cpa_name', 'cpa_firm', 'attorney_name', 'attorney_firm',
] as const;
export type OnboardingPrefillKey = (typeof ONBOARDING_PREFILL_KEYS)[number];

function packField(spec: { key: string; kind: FieldKind; label: string }): Record<string, unknown> {
  switch (spec.kind) {
    case 'yesNo':
      return { name: spec.key, type: 'boolean', widget: 'checkbox', required: false, label: spec.label };
    case 'date':
      return { name: spec.key, type: 'date', widget: 'date', required: false, label: spec.label };
    case 'longText':
      return {
        name: spec.key, type: 'string', widget: 'textarea', required: false, label: spec.label,
        validation: { maxLength: ONBOARDING_STRING_MAX },
      };
    case 'stringList':
      return { name: spec.key, type: 'array', widget: 'list', required: false, label: spec.label };
    default:
      return {
        name: spec.key, type: 'string', widget: 'text', required: false, label: spec.label,
        validation: { maxLength: 200 },
      };
  }
}

const SPEC_BY_KEY = new Map(
  [...ONBOARDING_FORM_SECTIONS.flatMap((s) => ONBOARDING_FORM_FIELDS[s]), ...LEGACY_SPECS].map((f) => [f.key, f] as const),
);

/** The `onboarding-details` pack fields: every section key, then every server-owned key. */
export const ONBOARDING_DETAILS_PACK_FIELDS: ReadonlyArray<Record<string, unknown>> = [
  ...ONBOARDING_SECTION_KEYS.map((k) => packField(SPEC_BY_KEY.get(k)!)),
  { name: ONBOARDING_SENT_AT_KEY, type: 'datetime', widget: 'datetime', required: false, label: 'Sent to the firm at' },
  { name: ONBOARDING_IDENTITY_PROVIDED_KEYS.client, type: 'boolean', widget: 'checkbox', required: false, label: 'Client SSN provided' },
  { name: ONBOARDING_IDENTITY_PROVIDED_KEYS.spouse, type: 'boolean', widget: 'checkbox', required: false, label: 'Spouse SSN provided' },
  ...ONBOARDING_FORM_SECTIONS.map((s) => ({
    name: ONBOARDING_SECTION_SAVED_KEYS[s], type: 'boolean', widget: 'checkbox', required: false,
    label: `${s} section saved`,
  })),
];

/** The `onboarding-prefill` pack fields: the prefillable keys only, same types as the details. */
export const ONBOARDING_PREFILL_PACK_FIELDS: ReadonlyArray<Record<string, unknown>> =
  // The legacy joined keys stay declared for the same reason as in the details type: prefill
  // rows already seeded on dev hold them (see {@link ONBOARDING_LEGACY_EMPLOYER_TITLE_KEYS}).
  [...ONBOARDING_PREFILL_KEYS, ...ONBOARDING_LEGACY_EMPLOYER_TITLE_KEYS].map((k) => packField(SPEC_BY_KEY.get(k)!));

/** The longest prefilled text value: the pack declares every prefill text field `maxLength: 200`. */
const PREFILL_TEXT_MAX = 200;

const prefillText = (v: unknown): string | undefined => {
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  // Omitted rather than cut: a truncated name or address is worse than an empty box, and
  // appcore's strict schema would refuse the whole row over one long value.
  return t === '' || t.length > PREFILL_TEXT_MAX ? undefined : t;
};

const asRecord = (v: unknown): Record<string, unknown> | undefined =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined;

/**
 * The `onboarding-prefill` attributes a snapshot's saved draft sections give
 * (crossmarc/steward-app#1765, design v2 §5; children added by #1812). Reads `aboutYou` and
 * `spousePartner` only, the sections whose answers the form asks for again; every other
 * snapshot answer stays where it is. It stopped reading `currentTeam` (cpa/attorney) in #1850,
 * since the client form no longer asks for either; {@link ONBOARDING_PREFILL_KEYS} still
 * declares `cpa_name`/`cpa_firm`/`attorney_name`/`attorney_firm` (see the module header), so
 * this function simply never fills them, rather than the pack refusing to accept them.
 *
 * Lenient on purpose: `sections` is a stored row's JSON, so anything unexpected is skipped
 * rather than thrown. The result holds only {@link ONBOARDING_PREFILL_KEYS}, and only values the
 * pack's strict schema admits, so a refresh can never be refused for its content.
 *
 * Employer and title are two fields on the form, as on the snapshot (#1904), so each is carried
 * on its own; there is no join. The spouse's answers are carried only when the snapshot says
 * there is a spouse. A child's name and date of birth are carried at the SAME index in their
 * respective list even when one of the two is blank (padded with `''`), so a later name always
 * lines up with the right date of birth; see the module header, "Children ... and CPA/attorney's
 * removal".
 */
export function onboardingPrefillFromDraftSections(
  sections: unknown,
): Partial<Record<OnboardingPrefillKey, string | boolean | string[]>> {
  const out: Partial<Record<OnboardingPrefillKey, string | boolean | string[]>> = {};
  const root = asRecord(sections);
  if (!root) return out;
  const person = (p: Record<string, unknown> | undefined, s: 'client' | 'spouse'): void => {
    if (!p) return;
    const put = (key: OnboardingPrefillKey, v: string | undefined) => { if (v !== undefined) out[key] = v; };
    put(`${s}_firstName`, prefillText(p['firstName']));
    put(`${s}_lastName`, prefillText(p['lastName']));
    put(`${s}_email`, prefillText(p['email']));
    put(`${s}_phone`, prefillText(p['phone']));
    put(`${s}_employer`, prefillText(p['employer']));
    put(`${s}_title`, prefillText(p['title']));
  };
  person(asRecord(root['aboutYou']), 'client');
  const sp = asRecord(root['spousePartner']);
  if (sp && typeof sp['hasSpousePartner'] === 'boolean') {
    out.spouse_included = sp['hasSpousePartner'];
    if (sp['hasSpousePartner']) person(asRecord(sp['spouse']), 'spouse');
  }
  const children = Array.isArray(sp?.['children']) ? (sp['children'] as unknown[]) : [];
  const childNames = children.map((c) => prefillText(asRecord(c)?.['name']) ?? '');
  const childDobs = children.map((c) => prefillText(asRecord(c)?.['dateOfBirth']) ?? '');
  // Omitted, like any other blank answer, when NEITHER list has anything to say — a child row
  // with no name and no date of birth is not a child worth prefilling.
  if (childNames.some((x) => x !== '') || childDobs.some((x) => x !== '')) {
    out.children_names = childNames;
    out.children_dateOfBirths = childDobs;
  }
  return out;
}

/** A key-by-key view of a section's answers. A `string[]` is the children lists (see the
 *  module header); everything else stays text or a yes/no. */
export type OnboardingAnswers = Record<string, string | boolean | string[]>;

/**
 * A copy of `a` where, for each of client and spouse whose `_employer` and `_title` are both
 * blank but whose legacy `_employerTitle` holds text, the legacy text stands in as the employer
 * (it cannot be split reliably, so it is shown whole rather than guessed at). Rows written before
 * the #1904 split are the only ones this changes.
 */
export function withLegacyEmployerTitle<T extends Record<string, unknown>>(a: T): T {
  let out: Record<string, unknown> | null = null;
  const blank = (v: unknown) => typeof v !== 'string' || v.trim() === '';
  for (const s of ['client', 'spouse'] as const) {
    const legacy = a[`${s}_employerTitle`];
    if (typeof legacy === 'string' && legacy.trim() !== '' && blank(a[`${s}_employer`]) && blank(a[`${s}_title`])) {
      out ??= { ...a };
      out[`${s}_employer`] = legacy.trim();
    }
  }
  return (out ?? a) as T;
}

const isAnswerValue = (v: unknown): v is string | boolean | string[] =>
  typeof v === 'string' || typeof v === 'boolean' || (Array.isArray(v) && v.every((x) => typeof x === 'string'));

/**
 * What the form shows. "Saved answers always win" (design v2 §5), per SECTION: once the client
 * has saved a section, that section shows only what they saved, and the prefill no longer
 * applies to it. Per section rather than per key because appcore stores a cleared answer by
 * removing the key: a per-key merge would bring back a prefilled value the client deliberately
 * erased. An unsaved section shows the prefill, marked so the form can say where it came from.
 *
 * A section counts as saved when it has any key present (the original rule) OR its
 * {@link ONBOARDING_SECTION_SAVED_KEYS} marker is `true` (crossmarc/steward-app#1812): a section
 * saved fully blank has no keys left, so without the marker it would be indistinguishable from
 * one never opened, and the prefill would silently come back on the next load. The union keeps
 * this correct for rows saved before the marker existed too, since those rows' real content
 * still trips the "any key" half.
 */
export function mergeOnboardingPrefill(
  saved: Record<string, unknown> | null | undefined,
  prefill: Record<string, unknown> | null | undefined,
): { values: OnboardingAnswers; fromPrefill: Set<string> } {
  saved = saved ? withLegacyEmployerTitle(saved) : saved;
  prefill = prefill ? withLegacyEmployerTitle(prefill) : prefill;
  const values: OnboardingAnswers = {};
  const fromPrefill = new Set<string>();
  const savedSections = new Set(
    Object.keys(saved ?? {}).map(onboardingSectionOfKey).filter((s): s is OnboardingFormSection => s !== null),
  );
  for (const s of ONBOARDING_FORM_SECTIONS) {
    if (saved?.[ONBOARDING_SECTION_SAVED_KEYS[s]] === true) savedSections.add(s);
  }
  // Each source's say per question, then the one precedence function (`answerPrecedence.ts`)
  // decides. The CLIENT (the saved row) speaks for every key of a section it has saved: a key
  // present with an answer is a value, and a key absent from a saved section is an explicit
  // empty (appcore stores a cleared answer by removing the key), which beats the prefill. An
  // unsaved section is absent for the client. The prefill speaks only through keys it holds.
  const client: Record<string, SourceAnswer<string | boolean | string[]>> = {};
  const prefillAnswers: Record<string, SourceAnswer<string | boolean | string[]>> = {};
  for (const [k, v] of Object.entries(prefill ?? {})) {
    const section = onboardingSectionOfKey(k);
    if (section === null) continue;
    if (isAnswerValue(v)) prefillAnswers[k] = { state: 'value', value: v };
    if (savedSections.has(section)) client[k] ??= { state: 'empty' };
  }
  for (const [k, v] of Object.entries(saved ?? {})) {
    if (onboardingSectionOfKey(k) === null) continue;
    if (isAnswerValue(v)) client[k] = { state: 'value', value: v };
  }
  for (const [k, r] of resolveAnswers({ client, prefill: prefillAnswers })) {
    if (r.state !== 'value') continue;
    values[k] = r.value;
    if (r.provenance.source === 'prefill') fromPrefill.add(k);
  }
  return { values, fromPrefill };
}
